/**
 * useShareAssets — generates share card PNGs from offscreen React components.
 * Uses html-to-image with caching + CORS-safe hero images + robust cleanup.
 * Supports single image and 3-slide carousel generation.
 */

import { useState, useCallback } from 'react';
import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { toPng } from 'html-to-image';
import { track } from '@/lib/analytics';
import { captureAppError } from '@/lib/monitoring/sentry';
import type { ShareTarget, ShareFormat, TemplateKey } from './ShareTarget';
import { SHARE_FORMAT_SPECS as SPECS } from './ShareTarget';
import { getTemplate, CAROUSEL_SLIDES } from './templates/registry';
import type { ShareCardProps } from './templates/ShareCardBase';
import type { ComponentType } from 'react';

// In-memory cache: key → data URL
const pngCache = new Map<string, string>();

function cacheKey(targetId: string, format: ShareFormat, templateKey: string, heroUrl: string | null): string {
  return `${targetId}:${format}:${templateKey}:${heroUrl || 'none'}`;
}

/** Wait for all <img> elements to load AND decode */
async function waitForImages(container: HTMLElement): Promise<void> {
  const imgs = Array.from(container.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      if (!img.complete) {
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });
      }
      try {
        if (typeof img.decode === 'function') {
          await img.decode();
        }
      } catch {
        // decode can throw if image is broken — non-blocking
      }
    }),
  );
}

/** Safe cleanup: unmount React root + remove DOM container */
function cleanup(root: Root | null, container: HTMLElement | null) {
  try { root?.unmount(); } catch { /* already unmounted */ }
  try {
    if (container?.parentNode) container.parentNode.removeChild(container);
  } catch { /* already removed */ }
}

/** Check if an error message indicates canvas tainting */
function isTaintError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /tainted|security|cross-origin|cors/i.test(msg);
}

const PIXEL_RATIO = Math.min(2, typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1);

/** Render a single component to PNG */
async function renderComponentToPng(
  Component: ComponentType<ShareCardProps>,
  props: ShareCardProps,
  spec: { w: number; h: number },
): Promise<string> {
  let container: HTMLElement | null = null;
  let root: Root | null = null;

  try {
    container = document.createElement('div');
    container.style.cssText = `position:fixed;left:-9999px;top:0;width:${spec.w}px;height:${spec.h}px;overflow:hidden;`;
    document.body.appendChild(container);

    root = createRoot(container);
    const element = createElement(Component, props);
    flushSync(() => root!.render(element));

    // Wait for paint
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await waitForImages(container);
    if (document.fonts?.ready) await document.fonts.ready;
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => setTimeout(r, 40)); // iOS safety

    const node = (container.firstElementChild as HTMLElement) ?? container;
    const dataUrl = await toPng(node, {
      width: spec.w,
      height: spec.h,
      pixelRatio: PIXEL_RATIO,
      cacheBust: true,
    });

    cleanup(root, container);
    root = null;
    container = null;
    return dataUrl;
  } catch (err) {
    cleanup(root, container);
    throw err;
  }
}

export interface GenerateResult {
  dataUrl: string;
  format: ShareFormat;
  templateKey: TemplateKey;
  retryWithoutHero?: boolean;
}

export interface CarouselResult {
  slides: { index: number; dataUrl: string }[];
  format: ShareFormat;
  retryWithoutHero?: boolean;
}

export function useShareAssets() {
  const [generating, setGenerating] = useState(false);

  /** Generate a single image */
  const generate = useCallback(
    async (
      target: ShareTarget,
      format: ShareFormat,
      templateKey: TemplateKey,
    ): Promise<GenerateResult | null> => {
      const key = cacheKey(target.id, format, templateKey, target.heroImageUrl);
      const cached = pngCache.get(key);
      if (cached) return { dataUrl: cached, format, templateKey };

      const template = getTemplate(templateKey);
      if (!template) return null;

      const spec = SPECS[format];
      const t0 = performance.now();

      setGenerating(true);

      try {
        const dataUrl = await renderComponentToPng(
          template.component,
          { target, format, spec },
          spec,
        );

        pngCache.set(key, dataUrl);

        track('share_asset_generated', {
          format, template_key: templateKey, success: true,
          duration_ms: Math.round(performance.now() - t0),
          target_type: target.type, pixel_ratio: PIXEL_RATIO,
          asset_type: 'single',
        });

        return { dataUrl, format, templateKey };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);

        // If CORS taint and we had a hero image, retry without it
        if (isTaintError(err) && target.heroImageUrl) {
          const fallbackTarget = { ...target, heroImageUrl: null };
          const fallbackKey = cacheKey(target.id, format, templateKey, null);
          const fallbackCached = pngCache.get(fallbackKey);
          if (fallbackCached) {
            track('share_asset_generated', {
              format, template_key: templateKey, success: true,
              duration_ms: Math.round(performance.now() - t0),
              target_type: target.type, retry_without_hero: true,
              asset_type: 'single',
            });
            return { dataUrl: fallbackCached, format, templateKey, retryWithoutHero: true };
          }
          setGenerating(false);
          const retryResult = await generate(fallbackTarget, format, templateKey);
          if (retryResult) return { ...retryResult, retryWithoutHero: true };
        }

        track('share_asset_generated', {
          format, template_key: templateKey, success: false,
          duration_ms: Math.round(performance.now() - t0),
          target_type: target.type,
          error_message: errorMessage.slice(0, 200),
          asset_type: 'single',
        });

        captureAppError(err instanceof Error ? err : new Error(errorMessage), {
          context: 'share_asset_generation',
          extra: { format, templateKey, targetType: target.type, heroImageUrl: target.heroImageUrl, pixelRatio: PIXEL_RATIO },
        });

        return null;
      } finally {
        setGenerating(false);
      }
    },
    [],
  );

  /** Generate 3 carousel slides (all 1080x1080) */
  const generateCarousel = useCallback(
    async (target: ShareTarget): Promise<CarouselResult | null> => {
      const format: ShareFormat = 'ig-post';
      const spec = SPECS[format];
      const t0 = performance.now();

      // Check cache for all 3
      const keys = CAROUSEL_SLIDES.map((s) => cacheKey(target.id, format, `carousel-${s.index}`, target.heroImageUrl));
      const allCached = keys.every((k) => pngCache.has(k));
      if (allCached) {
        return {
          slides: keys.map((k, i) => ({ index: i, dataUrl: pngCache.get(k)! })),
          format,
        };
      }

      setGenerating(true);
      let retryWithoutHero = false;

      try {
        const slides: { index: number; dataUrl: string }[] = [];

        for (const slide of CAROUSEL_SLIDES) {
          const slideKey = cacheKey(target.id, format, `carousel-${slide.index}`, target.heroImageUrl);
          const cached = pngCache.get(slideKey);
          if (cached) {
            slides.push({ index: slide.index, dataUrl: cached });
            continue;
          }

          try {
            const dataUrl = await renderComponentToPng(
              slide.component,
              { target, format, spec },
              spec,
            );
            pngCache.set(slideKey, dataUrl);
            slides.push({ index: slide.index, dataUrl });
          } catch (err) {
            // On taint for slide 0 (hero), retry without hero
            if (isTaintError(err) && slide.index === 0 && target.heroImageUrl) {
              retryWithoutHero = true;
              const fallbackTarget = { ...target, heroImageUrl: null };
              const dataUrl = await renderComponentToPng(
                slide.component,
                { target: fallbackTarget, format, spec },
                spec,
              );
              const fk = cacheKey(target.id, format, `carousel-${slide.index}`, null);
              pngCache.set(fk, dataUrl);
              slides.push({ index: slide.index, dataUrl });
            } else {
              throw err;
            }
          }

          track('share_asset_generated', {
            format, template_key: `carousel-${slide.index}`, success: true,
            duration_ms: Math.round(performance.now() - t0),
            target_type: target.type, pixel_ratio: PIXEL_RATIO,
            asset_type: 'carousel', slide_index: slide.index,
          });
        }

        return { slides, format, retryWithoutHero };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        track('share_asset_generated', {
          format, template_key: 'carousel', success: false,
          duration_ms: Math.round(performance.now() - t0),
          target_type: target.type,
          error_message: errorMessage.slice(0, 200),
          asset_type: 'carousel',
        });
        captureAppError(err instanceof Error ? err : new Error(errorMessage), {
          context: 'share_carousel_generation',
          extra: { targetType: target.type, heroImageUrl: target.heroImageUrl },
        });
        return null;
      } finally {
        setGenerating(false);
      }
    },
    [],
  );

  return { generate, generateCarousel, generating };
}

/** Download a data URL as a file. Returns false if blocked (e.g. iOS). */
export function downloadDataUrl(dataUrl: string, filename: string): boolean {
  try {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { try { document.body.removeChild(link); } catch { /* */ } }, 100);
    return true;
  } catch {
    return false;
  }
}

/** Open data URL in new tab (fallback for iOS) */
export function openInNewTab(dataUrl: string): void {
  const w = window.open();
  if (w) {
    w.document.write(`<img src="${dataUrl}" style="max-width:100%;height:auto;" />`);
    w.document.title = 'Farmly Share Image';
  }
}
